import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, ChevronRight, Film, FileText } from "lucide-react";

const STOPS = [
  {
    to: "/",
    title: "Command Center",
    note: "Executive AGRI score, KPIs, token & risk intelligence.",
    presenter:
      "Open with the AGRI score — 82 of 100. This single number tells your CIO whether AI is governed today. Walk them through the six KPIs and call out the token spend MTD.",
  },
  {
    to: "/discovery",
    title: "AI Discovery",
    note: "Every AI system — including shadow AI — accounted for.",
    presenter:
      "AI-ECO continuously scans for shadow AI. Anything new — like an unregistered support bot — surfaces here within minutes, ready to be registered with one click.",
  },
  {
    to: "/inventory",
    title: "AI Inventory & Policies",
    note: "Codified purpose, allowed data, blocked attributes.",
    presenter:
      "Every agent has a contract. Purpose, allowed actions, allowed data — and the data fields that must never be read. The Credit Risk Agent, for example, is forbidden from reading marital_status.",
  },
  {
    to: "/runtime",
    title: "Runtime Monitor",
    note: "Live workflow, policy enforcement and risk in motion.",
    presenter:
      "Watch the workflow execute. Sarah's loan flows green. James asks for 180K — exceeds policy — blocked. Priya triggers a privacy violation — escalated. Three outcomes, three governance decisions, three audit entries.",
  },
  {
    to: "/oversight",
    title: "Human Oversight",
    note: "Humans review the cases that matter — every override is audited.",
    presenter:
      "Humans only see the cases the policy engine cannot resolve alone. Approve, reject or request more information — every override is captured with a comment and a timestamp.",
  },
  {
    to: "/audit",
    title: "Audit Center",
    note: "Immutable trail. Exportable for regulators in one click.",
    presenter:
      "Finally, the audit trail. Cryptographically chained, fully searchable, exportable as a signed PDF. Your auditor's first question — who decided what, and when — answered.",
  },
];

const DWELL_MS = 7000;

export default function LiveDemoMode() {
  const [active, setActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [presenter, setPresenter] = useState(false);
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const loc = useLocation();
  const rafRef = useRef();
  const startRef = useRef();
  // Keep latest values accessible inside the rAF closure without re-binding.
  const pausedRef = useRef(paused);
  const progressRef = useRef(progress);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { progressRef.current = progress; }, [progress]);

  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    if (p.get("demo") === "1" && !active) {
      setActive(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("demo");
      window.history.replaceState({}, "", url);
    }
  }, [loc.search, active]);

  const stop = () => {
    setActive(false);
    setPaused(false);
    setIdx(0);
    setProgress(0);
  };

  useEffect(() => {
    if (!active) return;
    navigate(STOPS[idx].to, { replace: true });
    setProgress(0);
    startRef.current = performance.now();

    const tick = (t) => {
      if (pausedRef.current) {
        startRef.current = t - progressRef.current * DWELL_MS;
      } else {
        const p = Math.min(1, (t - startRef.current) / DWELL_MS);
        setProgress(p);
        if (p >= 1) {
          if (idx < STOPS.length - 1) setIdx((i) => i + 1);
          else stop();
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, idx, navigate]);

  const start = () => {
    setIdx(0);
    setProgress(0);
    setPaused(false);
    setActive(true);
  };
  const next = () => {
    if (idx < STOPS.length - 1) setIdx(idx + 1);
    else stop();
  };

  return (
    <>
      <button
        onClick={start}
        data-testid="start-live-demo"
        className="flex items-center gap-2 rounded-lg border border-[#5B8DEF]/30 bg-[#5B8DEF]/10 px-2.5 py-1.5 text-[12px] text-[#cfe0ff] hover:bg-[#5B8DEF]/15"
      >
        <Film size={13} strokeWidth={1.8} />
        Live Demo
      </button>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
            data-testid="demo-overlay"
          >
            <div className="w-[640px] max-w-[92vw] overflow-hidden rounded-2xl border border-white/10 bg-[#0f1730]/95 shadow-2xl backdrop-blur-xl">
              {presenter && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-white/5 bg-[#5B8DEF]/[0.04] px-5 py-3"
                  data-testid="presenter-notes"
                >
                  <div className="flex items-start gap-2">
                    <FileText size={13} className="mt-0.5 text-[#5B8DEF]" />
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#cfe0ff]">Speaker notes</div>
                      <div className="mt-1 text-[12.5px] leading-relaxed text-slate-200">{STOPS[idx].presenter}</div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div className="flex">
                <div className="flex flex-1 items-center gap-4 px-5 py-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#5B8DEF] to-[#22C55E]">
                    <Film size={16} className="text-[#0b1020]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display text-sm font-medium text-white">{STOPS[idx].title}</div>
                      <div className="font-mono text-[11px] text-slate-500">
                        {idx + 1} / {STOPS.length}
                      </div>
                    </div>
                    <div className="mt-0.5 text-[12px] text-slate-400">{STOPS[idx].note}</div>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/[0.06]">
                      <motion.div
                        className="h-full rounded-full"
                        animate={{ width: `${progress * 100}%` }}
                        transition={{ duration: 0.1, ease: "linear" }}
                        style={{ background: "linear-gradient(90deg,#5B8DEF,#22C55E)" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 border-l border-white/5 px-2">
                  <button
                    onClick={() => setPresenter((p) => !p)}
                    data-testid="demo-toggle-presenter"
                    className={`rounded-lg p-2 ${presenter ? "text-[#5B8DEF]" : "text-slate-300"} hover:bg-white/5`}
                    title="Presenter notes"
                  >
                    <FileText size={14} />
                  </button>
                  <button onClick={() => setPaused((p) => !p)} data-testid="demo-toggle-pause" className="rounded-lg p-2 text-slate-300 hover:bg-white/5">
                    {paused ? <Play size={14} /> : <Pause size={14} />}
                  </button>
                  <button onClick={next} data-testid="demo-next" className="rounded-lg p-2 text-slate-300 hover:bg-white/5">
                    <ChevronRight size={14} />
                  </button>
                  <button onClick={stop} data-testid="demo-stop" className="rounded-lg p-2 text-slate-300 hover:bg-white/5">
                    <X size={14} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
